import React, { Component } from 'react';
import { DateTime } from 'luxon';
import PropTypes from 'prop-types';
import moment from 'moment';

import FacilityHelper from '../helpers/FacilityHelper';
import StyleHelper from '../../shared/helpers/StyleHelper';
import CurrencyHelper from '../helpers/CurrencyHelper';

import SlotPopover from './SlotPopover';
import Tooltip from './Tooltip.jsx';

import './SlotsTimetable.css';
import TimetableHelper from '../helpers/TimetableHelper';
import { isScrolledToBottom } from '../../shared/utils/scroll';

/**
 * @typedef {{
 *   unavailableTimetableSlotStyle: Record<string, any>,
 * }} Props
 */

/**
 * @extends {Component<Props>}
 */
class SlotsTimetable extends Component {
  /**
   * @param {Props} props
   */
  constructor(props) {
    super(props);
    this.state = {
      show: [],
      width: null,
      currentHour: DateTime.local().hour,
      popoverId: null,
      scrollY: 0,
      hoursVisible: 3,
    };
    this.resizeFunction = null;
    this.resizeTimer = null;
    this.labelWidth = 150;
    this.rowHeight = 70;
    this.areaScroll = false;
    this.labelHeight = this.rowHeight + 2;
    this.numberOfDaysLookahead = this.props.numberOfDaysLookahead;
  }

  showPopover(popoverId) {
    this.setState({
      popoverId,
    });
  }

  hidePopover() {
    this.setState({
      popoverId: null,
    });
  }

  setCurrentHour(hour) {
    this.setState({
      currentHour: hour,
    });
  }

  toggleMore(identifier) {
    const show = this.state.show.slice();
    const index = show.indexOf(identifier);
    if (index < 0) {
      show.push(identifier);
    } else {
      show.splice(index, 1);
    }
    this.setState({
      show,
    });
  }

  handleResize() {
    const node = this.node;
    const { width } = node.getBoundingClientRect();
    let hoursVisible = this.state.hoursVisible;
    if (width >= 1550) {
      hoursVisible = 5;
    } else if (width >= 1250) {
      hoursVisible = 4;
    } else if (width >= 950) {
      hoursVisible = 3;
    } else {
      hoursVisible = 2;
    }
    if (node && typeof node.getBoundingClientRect === 'function') {
      this.setState({
        width,
        hoursVisible,
      });
    }
  }

  componentDidMount() {
    this.resizeFunction = () => {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => {
        this.handleResize();
      }, 250);
    };
    window.addEventListener('resize', this.resizeFunction);
    this.handleResize();
    this.scrollFunction = () => {
      clearTimeout(this.scrollTimer);
      this.scrollTimer = setTimeout(() => {
        this.setState({ ...this.state, scrollY: window.scrollY });
        this.handleScroll();
      }, 100);
    };
    window.addEventListener('scroll', this.scrollFunction);
    this.attachAreaScroll();
    this.firstScrollHandle = setTimeout(() => {
      this.handleScroll();
    }, 500);
  }

  componentWillUnmount() {
    clearTimeout(this.resizeTimer);
    clearTimeout(this.firstScrollHandle);
    window.removeEventListener('resize', this.resizeFunction);
    clearTimeout(this.scrollTimer);
    window.removeEventListener('scroll', this.scrollFunction);
    const node = this.area;
    if (node) {
      node.removeEventListener('scroll', this.scrollFunction);
    }
  }

  attachAreaScroll() {
    if (!this.areaScroll) {
      const node = this.area;
      if (node) {
        node.addEventListener('scroll', this.scrollFunction);
        this.areaScroll = true;
      }
    }
  }

  handleScroll() {
    // do nothing
    const isAtBottom = isScrolledToBottom(this.area);
    if (!isAtBottom && !this.state.tooltip) {
      this.setState({ tooltip: true });
    } else if (isAtBottom && this.state.tooltip) {
      this.setState({ tooltip: false });
    }
  }

  componentDidUpdate() {
    if (typeof this.props.didUpdate === 'function') {
      this.props.didUpdate();
    }
    this.attachAreaScroll();
    this.handleScroll();
  }

  createHourList(currentHour, startHour) {
    const hoursVisible = this.state.hoursVisible;
    const filter = this.props.filter || {};
    const isHourBranded = StyleHelper.applyCustomStyle(
      this.props.hourStyle,
      '.SlotsTimetable .hourList li.selected.branded'
    )
      ? 'branded'
      : '';

    const hourList = [];
    let hourIndex = 0;
    let hourTime = DateTime.fromISO(this.props.date).set({ hour: startHour, minute: 0 });
    let currentDay = hourTime.day;
    while (hourTime.day === currentDay) {
      const hour = hourTime.hour;
      if (FacilityHelper.isHourInTimeOfDayFilter(hour, filter)) {
        let setHour = hour;
        if (24 - setHour < hoursVisible) {
          setHour = 24 - hoursVisible;
        }
        hourList.push(
          <li
            key={hourIndex}
            className={`${hour === currentHour ? 'selected' : ''} ${isHourBranded}`}
            onClick={() => this.setCurrentHour(setHour)}
          >
            {hour}
          </li>
        );
      }
      hourTime = hourTime.plus({ hours: 1 });
      hourIndex += 1;
    }

    return hourList;
  }

  createHeadingList(currentHour) {
    const hoursVisible = this.state.hoursVisible;
    const hourWidth = (this.state.width - this.labelWidth) / hoursVisible;
    const headingList = [];
    let eventIndex = 0;
    let headingTime = DateTime.fromISO(this.props.date).set({ hour: currentHour, minute: 0 });
    for (let i = 0; i < hoursVisible; i += 1) {
      const gridStyle = {
        width: `${hourWidth}px`,
        left: `${i * hourWidth + this.labelWidth}px`,
      };
      headingList.push(
        <div key={eventIndex} className="timetable-time" style={gridStyle}>
          {headingTime.toLocaleString(DateTime.TIME_24_SIMPLE)}
        </div>
      );
      headingTime = headingTime.plus({ hours: 1 });
      eventIndex += 1;
    }
    return headingList;
  }

  render() {
    if (this.state.width !== null) {
      const slots = this.props.slots || [];
      const filter = this.props.filter || {};
      const timetableContainer = [];
      const slotsTimetable = [];
      const initialStartHour = FacilityHelper.getMinimumHour(slots);
      const hoursVisible = this.state.hoursVisible;
      const hourWidth = (this.state.width - this.labelWidth) / hoursVisible;
      const popover = {
        slot: null,
        subEvent: null,
      };

      let currentHour = this.state.currentHour;
      let maxHour = 23;

      if (slots.length === 0) {
        this.areaScroll = false;
        return (
          <div ref={(node) => (this.node = node)} className="NoResults">
            <span>
              <p>There are presently no slots available for this facility.</p>
              <p>Please try another facility.</p>
            </span>
          </div>
        );
      }

      const wasAvailableTimetableSlotStyleApplied = StyleHelper.applyCustomStyle(
        this.props.customStyle,
        '.SlotsTimetable .timetable-row .timetable-slot.branded'
      );
      const wasUnavailableTimetableSlotStyleApplied = StyleHelper.applyCustomStyle(
        this.props.unavailableTimetableSlotStyle,
        '.SlotsTimetable .timetable-row .timetable-slot.branded.unavailable'
      );
      const branded =
        wasAvailableTimetableSlotStyleApplied || wasUnavailableTimetableSlotStyleApplied
          ? 'branded'
          : '';

      let startHour;

      if (FacilityHelper.hasTimeOfDayFilter(filter)) {
        for (let i = initialStartHour; i < 24; i += 1) {
          if (FacilityHelper.isHourInTimeOfDayFilter(i, filter)) {
            if (!startHour) {
              startHour = i;
            }
            maxHour = i;
          }
        }
      } else {
        startHour = initialStartHour;
      }

      if (currentHour < startHour || currentHour > maxHour) {
        currentHour = startHour;
      }

      const hourList = this.createHourList(currentHour, startHour);
      const headingList = this.createHeadingList(currentHour);

      timetableContainer.push(
        <ul key={0} className="hourList">
          {hourList}
        </ul>
      );

      timetableContainer.push(
        <div key={1} className="timetable-header">
          {headingList}
        </div>
      );

      const buckets = {};
      for (let daysAhead = 0; daysAhead < this.numberOfDaysLookahead; daysAhead++) {
        const day = moment().add(daysAhead, 'days');
        const date = day.format('YYYY-MM-DD');
        const dayText = day.format('ddd');
        const dateText = day.format('DD MMM');

        const filteredSlots = FacilityHelper.filterSlotsByDate(slots, date);
        const rows = TimetableHelper.bucketSlots(filteredSlots);

        buckets[date] = {
          day: {
            dayText,
            dateText,
          },
          rows,
        };
      }
      let index = 2;
      let rowCount = 0;

      for (const bucketName in buckets) {
        if (Object.prototype.hasOwnProperty.call(buckets, bucketName)) {
          const { day, rows } = buckets[bucketName];
          const labelStyle = {
            width: `${this.labelWidth + 2}px`,
            top: `${rowCount * this.labelHeight}px`,
            height: `${rows.length * this.labelHeight}px`,
          };
          slotsTimetable.push(
            <div key={`timetableLabel-${index}`} className="timetable-label" style={labelStyle}>
              <div>{day.dayText}</div>
              <div>{day.dateText}</div>
            </div>
          );
          index++;
          let eventIndex = 0;
          for (const row of rows) {
            rowCount++;
            let slotList = [];
            for (let i = 0; i < hoursVisible; i += 1) {
              const gridStyle = {
                width: `${hourWidth + 2}px`,
                left: `${(i + currentHour - startHour) * hourWidth}px`,
              };
              slotList.push(
                <div
                  key={`timetableRow-${rowCount}-timetableGrid-${i}`}
                  className="timetable-grid"
                  style={gridStyle}
                ></div>
              );
            }
            for (const slot of row) {
              const startTime = DateTime.fromISO(slot.startDate);
              let endTime = DateTime.fromISO(slot.endDate);
              if (endTime.second === 59) {
                endTime = endTime.plus({ seconds: 1 });
              }
              let itemStartHour = startTime.hour;
              let itemStartMinute = startTime.minute;
              const itemEndHour = endTime.hour;

              if (
                itemStartHour < currentHour &&
                (itemEndHour > currentHour || (itemEndHour === currentHour && endTime.minute > 0))
              ) {
                itemStartHour = currentHour;
                itemStartMinute = 0;
              }

              const leftOffset = (itemStartHour - startHour + itemStartMinute / 60) * hourWidth;

              const width =
                (itemEndHour - startHour + endTime.minute / 60) * hourWidth - leftOffset;

              const slotStyle = {
                width: `${width - 2}px`,
                left: `${leftOffset + 2}px`,
              };

              const duration = moment.duration(slot.duration);
              let formattedDuration = '';

              if (duration.as('hours') === 1) {
                formattedDuration += `${duration.as('hours')} hour, `;
              } else if (duration.as('hours') > 1) {
                const minutes = (duration.as('hours') % 1) * 60;
                formattedDuration += `${Math.floor(duration.as('hours'))} hours, `;
                if (minutes) {
                  formattedDuration += `${minutes} minutes`;
                }
              } else {
                formattedDuration += `${duration.as('minutes')} minutes`;
              }
              formattedDuration = formattedDuration.replace(/, $/, '');

              const startTimeString = DateTime.fromISO(slot.startDate).toLocaleString(
                DateTime.TIME_24_SIMPLE
              );
              const publicAdultAggregateOffer =
                slot['imin:aggregateOffer'] && slot['imin:aggregateOffer'].publicAdult;
              slotList.push(
                <div
                  key={eventIndex}
                  title={slot.startDate}
                  className={`timetable-slot ${branded} ${
                    slot.remainingUses > 0 ? '' : 'unavailable'
                  }`}
                  onClick={() => this.showPopover(slot.identifier)}
                  style={slotStyle}
                >
                  <span className="slotName">
                    {startTimeString} - {formattedDuration}
                  </span>
                  <span className="slotPrice">
                    {this.props.showPrices && publicAdultAggregateOffer
                      ? CurrencyHelper.format(
                          'en-GB',
                          publicAdultAggregateOffer.priceCurrency,
                          publicAdultAggregateOffer.price
                        )
                      : ''}
                  </span>
                  {!this.props.shouldShowNoAvailability && (
                    <span className="sessionAvailability">{slot.remainingUses} spaces</span>
                  )}
                </div>
              );

              if (slot.identifier === this.state.popoverId) {
                popover.slot = slot;
              }

              eventIndex += 1;
            }
            const rowStyle = {
              width: `${(24 - startHour) * hourWidth}px`,
              left: `${(startHour - currentHour) * hourWidth + this.labelWidth}px`,
            };
            if (row.length === 0) {
              rowStyle.backgroundImage =
                'repeating-linear-gradient(-45deg, rgba(224, 224, 224, 0.6)0px 30px, #fff 0 40px)';
            }
            slotsTimetable.push(
              <div key={`timetableRow-${rowCount}`} className="timetable-row" style={rowStyle}>
                {slotList}
              </div>
            );
          }
        }
      }

      let slotPopover;
      if (popover.slot) {
        slotPopover = (
          <SlotPopover
            facility={this.props.facility}
            slot={popover.slot}
            checkoutTheme={this.props.checkoutTheme}
            checkoutUrl={this.props.checkoutUrl}
            baseMemberBookUrl={this.props.baseMemberBookUrl}
            buttonStyle={this.props.buttonStyle}
            memberBookButtonStyle={this.props.memberBookButtonStyle}
            onHide={() => this.hidePopover()}
            scrollY={this.state.scrollY}
            shouldShowBookAsGuestButton={this.props.shouldShowBookAsGuestButton}
            shouldShowBookAsMemberButton={this.props.shouldShowBookAsMemberButton}
            memberDeepLinks={this.props.memberDeepLinks}
            bookableProviders={this.props.bookableProviders}
            shouldShowNoAvailability={this.props.shouldShowNoAvailability}
            showPrices={this.props.showPrices}
          />
        );
      }

      const ttStyle = {};
      if (this.props.height) {
        /* We make room for the:
        - Filters
        - hoursList
        - timetable-header
        - Space at the bottom for the "scroll down for more" tooltip */
        ttStyle.maxHeight = `${this.props.height - 15}rem`;
      }

      let tooltip;
      if (this.state.tooltip) {
        tooltip = <Tooltip title="Scroll down for more slots" />;
      }

      return (
        <div ref={(node) => (this.node = node)}>
          {slotPopover}
          <div className="SlotsTimetable">
            {timetableContainer}
            <div ref={(area) => (this.area = area)} className="timetable-area" style={ttStyle}>
              {slotsTimetable}
            </div>
          </div>
          {tooltip}
        </div>
      );
    }
    this.areaScroll = false;
    return <div ref={(node) => (this.node = node)} className="SlotsTimetable"></div>;
  }
}

SlotsTimetable.propTypes = {
  didUpdate: PropTypes.func,
  date: PropTypes.string,
  facility: PropTypes.object,
  slots: PropTypes.array,
  filter: PropTypes.object,
  customStyle: PropTypes.object,
  unavailableTimetableSlotStyle: PropTypes.object,
  hourStyle: PropTypes.object,
  checkoutUrl: PropTypes.string,
  baseMemberBookUrl: PropTypes.string,
  checkoutTheme: PropTypes.string,
  buttonStyle: PropTypes.object,
  memberBookButtonStyle: PropTypes.object,
  height: PropTypes.number,
  shouldShowBookAsGuestButton: PropTypes.bool,
  shouldShowBookAsMemberButton: PropTypes.bool,
  memberDeepLinks: PropTypes.object,
  bookableProviders: PropTypes.array,
  shouldShowNoAvailability: PropTypes.bool,
  showPrices: PropTypes.bool,
  numberOfDaysLookahead: PropTypes.number,
};

export default SlotsTimetable;
